import React, { useEffect, useState } from "react";
import ClockIcon from "@mui/icons-material/Schedule";
import { USER_ROLES } from "../../../utilities/constant";
import { Users, PhoneCall, ListChecks, Server, ArrowRightCircle } from "lucide-react";


const Boxes = ({ realtimeData , setQueuePopupVisible, queueDetail, loggedInUser}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    typeof document !== "undefined" && document.body.classList.contains("dark")
  );

  useEffect(() => {
    const updateSize = () => setIsMobile(window.innerWidth <= 768);
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const body = document.body;
    const updateTheme = () => setIsDarkMode(body.classList.contains("dark"));
    const observer = new MutationObserver(updateTheme);

    updateTheme();
    observer.observe(body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const outerContainerStyle = {
    overflowX: "auto",
    padding: "8px 0",
    WebkitOverflowScrolling: "touch",
  };

const innerContainerStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "stretch",
  justifyContent: "space-between",
  padding: isMobile ? "4px" : "8px",
  gap: isMobile ? "8px" : "16px",
  width: "100%",
};

const cardStyle = {
  borderRadius: "12px",
  boxShadow: isDarkMode
    ? "0 14px 32px rgba(2, 6, 23, 0.34)"
    : "0 3px 8px rgba(15, 23, 42, 0.12)",
  transition: "all 0.2s ease",
  borderTop: "5px solid",
  flex: "1 1 280px",
  minWidth: "260px",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "6px 8px 10px",
};

const headerContainerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "4px 6px 4px 8px",
};

const headingStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 600,
  fontSize: "18px",
  color: isDarkMode ? "#f8fafc" : "#111827",
};

const getColor = (index) => {
  const colors = isDarkMode
    ? ["#f87171", "#4ade80", "#2dd4bf", "#a78bfa"]
    : ["#FF6B6B", "#1fab02", "#4ECDC4", "#841dcc"];
  return colors[index % colors.length];
};

const getBoxColor = (index) => {
  const colors = isDarkMode
    ? ["#1f2937", "#17261d", "#10272a", "#211637"]
    : ["#ffe2e2", "#ecfbe6", "#eaf7f6", "#f3ebfa"];
  return colors[index % colors.length];
};

const getInnerBoxColor = (index) => {
  const colors = isDarkMode
    ? ["#2a1b23", "#1b2b1f", "#152d31", "#2a2140"]
    : ["#ffb3b3", "#bee8b6", "#b6ddda", "#ccb2f0"];
  return colors[index % colors.length];
};

const getIconComponent = (label) => {
  switch (label) {
    case "Agent":
      return Users;
    case "Rings":
      return PhoneCall;
    case "Queue":
      return ListChecks;
    case "Trunks":
      return Server;
    default:
      return null;
  }
};

  const stats = [
  {
    value: [
      { label: "Total", value: `${realtimeData.length}` },
      { label: "On Call", value: `${realtimeData.filter(item => item.agent?.login_status === "2").length}` },
      { label: "Online", value: `${realtimeData.filter(item => item.agent?.login_status === "1").length}` },
      { label: "Offline", value: `${realtimeData.filter(item => item.agent?.login_status === "0").length}` },
    ],
    label: "Agent",
  },
  {
    value: [
      { label: "Total", value: `00:00:00` },
      { label: "Average", value: "00:00:00" },
      { label: "Maximum", value: "00:00:00" },
    ],
    label: "Rings",
  },
  {
    value: [
      { label: "Waiting", value: `${queueDetail?.count || 0}` },
      { label: "Average", value: `${queueDetail?.average || "00:00:00"}` },
      { label: "Maximum", value: "00:00:00" },
      { label: "Max Delay", value: "00:02:49" },
      { label: "button", value: true },
    ],
    label: "Queue",
  },
  ...(loggedInUser !== USER_ROLES.CALL_CENTRE_ADMIN
    ? [
        {
          value: [
            { label: "Total", value: 2 },
            { label: "On Call", value: 1 },
            { label: "Idle", value: 1 },
            { label: "Offline", value: 0 },
          ],
          label: "Trunks",
        },
      ]
    : []),
];


  return (
    <div style={outerContainerStyle}>
      <div style={innerContainerStyle}>
        {stats.map((stat, index) => {
          const color = getColor(index);
          const isQueueCard = stat.label === "Queue";
          const IconComponent = getIconComponent(stat.label);
          return (
            <div
              key={index}
              style={{
                ...cardStyle,
                backgroundColor: getBoxColor(index),
                borderTopColor: color,
              }}
            >
              {/* Header: title + icon (and Queue button) */}
              <div style={headerContainerStyle}>
                <div style={headingStyle}>
                  {IconComponent && <IconComponent size={22} />}
                  <span>{stat.label}</span>
                </div>
                {isQueueCard && (
                  <button
                    style={{
                      padding: "4px 12px",
                      borderRadius: 6,
                      fontSize: "13px",
                      background: "#1e3a8a",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 3px 8px rgba(30, 58, 138, 0.35)",
                    }}
                    onClick={() => setQueuePopupVisible(true)}
                  >
                    View Details
                  </button>
                )}
              </div>

              {/* Inner card with metrics */}
              <div
                style={{
                  // border:"2px solid red",
                  backgroundColor: getInnerBoxColor(index),
                  borderRadius: "10px",
                  // Move spacing to be mainly between heading and card,
                  // and remove extra space at the bottom of the outer card
                  margin: "12px 0px 0",
                  padding: "10px 20px 16px",
                  boxShadow: isDarkMode
                    ? "inset 0 0 0 1px rgba(148, 163, 184, 0.12), 0 10px 24px rgba(2, 6, 23, 0.25)"
                    : "0 8px 18px rgba(15, 23, 42, 0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginTop: "auto",
                  minHeight: "180px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    rowGap: "8px",
                    columnGap: "24px",
                    fontSize: "14px",
                  }}
                >
                  {stat.value.map((item, idx) => {
                    const isTimeFormat = /^\d{2}:\d{2}:\d{2}$/.test(item.value);
                    const showButton = item.label === "button" && item.value === true;

                    // Skip rendering the placeholder button cell,
                    // as View Details is now in the header for Queue.
                    if (showButton && isQueueCard) {
                      return null;
                    }

                    const cellStyle = {
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: showButton && isQueueCard ? "center" : "flex-start",
                      alignItems: showButton && isQueueCard ? "center" : "flex-start",
                      minHeight: showButton && isQueueCard ? "32px" : "auto",
                      gridColumn: showButton && isQueueCard ? "1 / -1" : "auto",
                    };

                    return (
                      <div key={idx} style={cellStyle}>
                        {!showButton && (
                          <span
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: 600,
                                fontSize: "15px",
                                color: isDarkMode ? "#dbeafe" : "#0f172a",
                              }}
                            >
                              <ArrowRightCircle size={16} />
                              <span>{item.label}</span>
                            </span>
                            <strong
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: isDarkMode ? "#f8fafc" : "#111827",
                              }}
                            >
                              {isTimeFormat && (
                                <ClockIcon style={{ fontSize: "16px", color: "currentColor" }} />
                              )}
                              {item.value}
                            </strong>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Boxes;
